<script lang="ts">
  import lottie, { type AnimationItem } from "lottie-web/build/player/lottie_light";
  import { onDestroy, onMount } from "svelte";

  export let name: string;
  export let autoplay: boolean = true;
  export let loop: boolean = true;
  export let speed: number = 1;
  export let playing: boolean = autoplay;
  export let width: string | number = "100%";
  export let height: string | number = "100%";

  let containerElement: HTMLDivElement;
  let animInstance: AnimationItem | null = null;

  $: if (playing) {
    player()?.play();
  } else {
    player()?.stop();
  }

  export const player = () => animInstance;

  onMount(async () => {
    if (!containerElement) return;

    try {
      const cleanName = name.replace(/\.(json|lottie)$/, "");

      // Use a Vite dynamic import to pull the JSON data safely.
      // This bypasses lottie-web's buggy internal fetch mechanism entirely.
      const module = await import(`../assets/${cleanName}.json`);
      const animationJson = module.default;

      // CRITICAL: Deep clone the JSON to prevent lottie-web from mutating the source in-place,
      // which causes a severe memory leak/progressive lag when reusing this asset.
      const animationDataCopy = JSON.parse(JSON.stringify(animationJson));

      // Initialize lottie-web
      animInstance = lottie.loadAnimation({
        container: containerElement,
        renderer: "svg",
        loop,
        autoplay,
        animationData: animationDataCopy,
        rendererSettings: {
          progressiveLoad: true,
          hideOnTransparent: true
        }
      });

      if (speed !== 1) {
        animInstance.setSpeed(speed);
      }
    } catch (error) {
      console.error("Error loading Lottie animation:", error);
    }
  });

  onDestroy(() => {
    if (animInstance) {
      animInstance.destroy();
      animInstance = null;
    }
  });
</script>

<div
  class="lottie-container"
  bind:this={containerElement}
  style:width={typeof width === "number" ? `${width}px` : width}
  style:height={typeof height === "number" ? `${height}px` : height}>
</div>

<style>
  .lottie-container {
    display: block;
    overflow: hidden;
  }

  .lottie-container :global(svg) {
    width: 100%;
    height: 100%;
    display: block;
  }
</style>
